import type { Knowledge } from "api/api.types";
import { InfinityLoader } from "common/components";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import type { TrackPage } from "domains/analytics/types";
import { getTopicsFilterSelectedIds } from "domains/monitor/filter/selectors";
import { PublicationListItem } from "domains/publication/components/PublicationListItem";
import { useMedia } from "domains/viewport/useMedia";

type PublicationListProps = {
  emptyMessage?: string;
  hasNextPage?: boolean;
  isPending?: boolean;
  publications: Knowledge[];
  lockTiles?: boolean;
  selectedId?: string;
  showEmptyMessage?: boolean;
  trackPage: TrackPage;
  getToLink: (id: string) => string;
  loadNextPage?: () => void;
};

const LATEST_KNOWLEDGE_PUBLIC_LIMIT = 4;

export const PublicationList = ({
  publications,
  hasNextPage = false,
  isPending = false,
  selectedId,
  trackPage,
  lockTiles,
  emptyMessage,
  showEmptyMessage = true,
  loadNextPage,
  getToLink,
}: PublicationListProps) => {
  const isList = useMedia({ small: true, medium: true }, false);
  const selectedTopics = useSelector(getTopicsFilterSelectedIds);
  const translate = useTranslate();

  if (showEmptyMessage && publications.length === 0 && !isPending) {
    if (emptyMessage) {
      return <p>{emptyMessage}</p>;
    }
    return (
      <p>{translate(selectedTopics.length === 0 ? "app.monitor.empty_message" : "app.monitor.empty_message_filter")}</p>
    );
  }

  return (
    <InfinityLoader isPending={isPending} hasNextPage={hasNextPage} loadNextPage={loadNextPage}>
      {publications.map((publication, index) => (
        <PublicationListItem
          title={publication.title}
          description={publication.summary}
          imageUrl={publication.images?.x_small}
          source={publication.source}
          timestamp={publication.time}
          key={publication.id}
          link={getToLink(publication.id)}
          isSelected={!isList && selectedId === publication.id}
          isLocked={lockTiles && index >= LATEST_KNOWLEDGE_PUBLIC_LIMIT}
          trackPage={trackPage}
          isFirstLocked={index === LATEST_KNOWLEDGE_PUBLIC_LIMIT}
        />
      ))}
    </InfinityLoader>
  );
};
